import { companyServices } from '@/services';

const {
  getCompanyList,
  getCompanyProfile,
  getKybChecklist,
  getCompanyDocuments,
  getNewCompanies,
  getChangedCompanies,
  getCompanyLoanRequests,
  getCompaniesCount,
  createCompany,
  verifyOrReverifyCompany,
  verifyKybItem,
  uploadCompanyDocument,
  getCompanyWallets,
  editCompany,
  updateCompanyLogo,
  getUnpaginatedBusinessList,
  exportCompaniesList,
} = companyServices;

export default {
  namespaced: true,
  state: () => ({
    companyList: [],
    newCompanies: [],
    changedCompanies: [],
    newCompaniesCount: 0,
    changedCompaniesCount: 0,
    currentCompany: null,
  }),
  getters: {
    companyList: ({ companyList }) => companyList,
    companiesPendingApproval: ({ companiesPendingApproval }) => companiesPendingApproval,
    newCompanies: ({ newCompanies }) => newCompanies,
    newCompaniesCount: ({ newCompaniesCount }) => newCompaniesCount,
    changedCompanies: ({ changedCompanies }) => changedCompanies,
    changedCompaniesCount: ({ changedCompaniesCount }) => changedCompaniesCount,
    currentCompany: ({ currentCompany }) => currentCompany,
  },
  mutations: {
    SET_COMPANY_LIST: (state, data) => {
      state.companyList = data;
    },
    SET_COMPANIES_PENDING_APPROVAL: (state, data) => {
      state.companiesPendingApproval = data;
    },
    SET_NEW_COMPANIES: (state, data) => {
      state.newCompanies = data;
    },
    SET_CHANGED_COMPANIES: (state, data) => {
      state.changedCompanies = data;
    },
    SET_NEW_COMPANIES_COUNT: (state, data) => {
      state.newCompaniesCount = data;
    },
    SET_CHANGED_COMPANIES_COUNT: (state, data) => {
      state.changedCompaniesCount = data;
    },
    SET_CURRENT_COMPANY: (state, data) => {
      state.currentCompany = data;
    },
  },
  actions: {
    async getCompanyList({ commit, dispatch }, query) {
      try {
        const { data } = await getCompanyList(
          query?.page || 1,
          query?.pageSize || 10,
          query?.search || '',
          query?.isRegistered,
          query?.isPlatformMerchant || '',
          query?.status || '',
          query?.startAt || '',
          query?.endAt || '',
          query?.referralSource || '',
          query?.registeredPlatform || '',
          query?.onboardingSteps || [],
          query?.sortBy || '',
          query?.orderBy || ''
        );

        commit('SET_COMPANY_LIST', data.data);
        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getNewCompanies({ commit, dispatch }, query) {
      try {
        const { data } = await getNewCompanies(
          query?.page || 1,
          query?.pageSize || 10,
          query?.search || ''
        );

        commit('SET_NEW_COMPANIES', data);
        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getChangedCompanies({ commit, dispatch }, query) {
      try {
        const { data } = await getChangedCompanies(
          query?.page || 1,
          query?.pageSize || 10,
          query?.search || ''
        );

        commit('SET_CHANGED_COMPANIES', data);
        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCompaniesCount({ commit, dispatch }) {
      try {
        const { data } = await getCompaniesCount();

        commit('SET_NEW_COMPANIES_COUNT', data.new);
        commit('SET_CHANGED_COMPANIES_COUNT', data.changed);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCompanyProfile({ dispatch }, id) {
      try {
        const { data } = await getCompanyProfile(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getKybChecklist({ dispatch }, id) {
      try {
        const { data } = await getKybChecklist(id);

        function getDocumentStatus(documents = [], type) {
          const document = documents.find((doc) => doc.type === type);

          if (!document || !document.isUploaded) {
            return 'Empty';
          }

          return document.isApproved ? 'Verified' : 'Not Verified';
        }

        const kybChecklist = {
          score: data.score,
          fields: [
            {
              label: 'RC Number',
              status: data?.hasRCNumber
                ? data?.hasRCNumberVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: data?.hasRCNumber ? 'verifyRcNumber' : 'addRcNumber',
              businessType: ['LLC', 'Partnership', 'SoleProprietorship'],
            },
            {
              label: 'Business Utility Bill',
              status: getDocumentStatus(data.documents, 'UtilityBill'),
              modalComponent: 'utilityBill',
              businessType: ['LLC', 'Partnership', 'SoleProprietorship'],
            },
            {
              label: 'Business Address',
              status: data?.hasAddress
                ? data?.hasAddressVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: 'verifyBusinessAddress',
              businessType: ['LLC', 'Partnership', 'SoleProprietorship'],
            },
            {
              label: "1st Director's BVN",
              status: data?.hasFirstDirectorBVN
                ? data?.hasFirstDirectorBVNVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: 'firstDirectorBvn',
              businessType: ['LLC'],
            },
            {
              label: "2nd Director's BVN",
              status: data?.hasSecondDirectorBVN
                ? data?.hasSecondDirectorBVNVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: 'secondDirectorBvn',
              businessType: ['LLC'],
            },
            {
              label: "1st Partner's BVN",
              status: data?.hasFirstDirectorBVN
                ? data?.hasFirstDirectorBVNVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: 'firstPartnerBvn',
              businessType: ['Partnership'],
            },
            {
              label: "2nd Partner's BVN",
              status: data?.hasSecondDirectorBVN
                ? data?.hasSecondDirectorBVNVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: 'secondPartnerBvn',
              businessType: ['Partnership'],
            },
            {
              label: "Owner's BVN",
              status: data?.hasOwnerBVN
                ? data?.hasOwnerBVNVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              modalComponent: 'ownerBvn',
              businessType: ['SoleProprietorship'],
            },
            {
              label: "1st Director's ID",
              status: getDocumentStatus(data.documents, 'FirstDirectorId'),
              modalComponent: 'firstDirectorId',
              businessType: ['LLC'],
            },
            {
              label: "2nd Director's ID",
              status: getDocumentStatus(data.documents, 'SecondDirectorId'),
              modalComponent: 'secondDirectorId',
              businessType: ['LLC'],
            },
            {
              label: "1st Partner's ID",
              status: getDocumentStatus(data.documents, 'FirstPartnerId'),
              modalComponent: 'firstPartnerId',
              businessType: ['Partnership'],
            },
            {
              label: "2nd Partner's ID",
              status: getDocumentStatus(data.documents, 'SecondPartnerId'),
              modalComponent: 'secondPartnerId',
              businessType: ['Partnership'],
            },
            {
              label: "Owner's ID",
              status: getDocumentStatus(data.documents, 'OwnerId'),
              modalComponent: 'ownerId',
              businessType: ['SoleProprietorship'],
            },
            {
              label: 'CAC 2A & 7A',
              status: getDocumentStatus(data.documents, 'CacCertificate'),
              modalComponent: 'cac',
              businessType: ['LLC', 'Partnership', 'SoleProprietorship'],
            },
            {
              label: 'Memart',
              status: getDocumentStatus(data.documents, 'Memart'),
              modalComponent: 'memart',
              businessType: ['LLC'],
            },
            {
              label: 'Deed of Partnership',
              status: getDocumentStatus(data.documents, 'PartnershipDeed'),
              modalComponent: 'partnershipDeed',
              businessType: ['Partnership'],
            },
            {
              label: 'Board Resolution Doc',
              status: getDocumentStatus(data.documents, 'BoardResolution'),
              modalComponent: 'boardResolutionDoc',
              businessType: ['LLC'],
            },
          ],
        };

        return kybChecklist;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCompaniesLite({ dispatch }) {
      try {
        const { data } = await getUnpaginatedBusinessList();

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCompanyDocuments({ dispatch }, id) {
      try {
        const { data } = await getCompanyDocuments(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCompanyLoanRequests({ dispatch }, id) {
      try {
        const { data } = await getCompanyLoanRequests(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCompanyWallet({ dispatch }, id) {
      try {
        const { data } = await getCompanyWallets(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async verifyOrReverifyCompany({ dispatch }, payload) {
      try {
        const data = await verifyOrReverifyCompany(
          payload.businessProfileId,
          payload.verificationType
        );

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            {
              message: `Company ${
                payload.verificationType === 'verify' ? 'verified' : 'reverified'
              }`,
              type: 'success',
            },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async verifyKybItem({ dispatch }, payload) {
      try {
        const formPayload = new FormData();

        formPayload.append('item', payload.data.item);
        formPayload.append('isVerified', payload.data.isVerified);

        if (payload.data.verificationProof) {
          for (const [key, value] of payload.data.verificationProof.entries()) {
            formPayload.append(key, value);
          }
        }

        const data = await verifyKybItem(formPayload, payload?.companyId);

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            { message: payload.successMessage, type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;

        dispatch(
          'notifications/displayNotification',
          {
            message: message ? message : statusText ? statusText : 'An error occurred',
            type: 'danger',
          },
          { root: true }
        );
      }
    },
    async uploadCompanyDocument({ dispatch }, payload) {
      try {
        const data = await uploadCompanyDocument(payload);

        if (data.status === 201) {
          dispatch(
            'notifications/displayNotification',
            {
              message: 'Document upload successful',
              type: 'success',
            },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async createCompany({ dispatch }, companyData) {
      try {
        const data = await createCompany(companyData);

        if (data.status === 201) {
          dispatch(
            'notifications/displayNotification',
            { message: `Added ${data?.data?.businessName}`, type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },

    async editCompanyDetails({ dispatch }, companyData) {
      try {
        const data = await editCompany(companyData);

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            { message: `Updated company details`, type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },

    async updateLogo({ dispatch }, pictureData) {
      try {
        const data = await updateCompanyLogo(pictureData);

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            { message: `Updated company logo`, type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },

    async exportCompanies({ dispatch }, query) {
      try {
        await exportCompaniesList(
          query?.type || 'pdf',
          query?.page || 1,
          query?.pageSize || 10,
          query?.search || '',
          query?.isRegistered,
          query?.isPlatformMerchant || '',
          query?.status || '',
          query?.startAt || '',
          query?.endAt || '',
          query?.referralSource || '',
          query?.registeredPlatform || '',
          query?.onboardingSteps || [],
          query?.sortBy || '',
          query?.orderBy || ''
        );

        dispatch(
          'notifications/displayNotification',
          { message: 'Export successful', type: 'success' },
          { root: true }
        );
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
  },
};
